import { Controller } from "@hotwired/stimulus"
import { pairGroups } from "../lib/combinatorics"

export default class extends Controller {
  static targets = ["point", "pair", "group", "firstSlot", "secondSlot", "currentPair", "count", "sum", "status", "button", "conclusion"]

  connect() {
    this.groups = pairGroups(this.pointTargets.map((point) => point.dataset.value))
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.reset()
  }

  disconnect() {
    window.clearTimeout(this.timer)
  }

  toggle() {
    if (this.running) {
      this.pause()
      return
    }

    if (this.finished) this.reset()
    this.play()
  }

  play() {
    this.running = true
    this.buttonTarget.textContent = "暂停演示"

    if (this.nextAction) {
      const action = this.nextAction
      this.schedule(action, 80)
    } else {
      this.started = true
      this.beginGroup()
    }
  }

  pause() {
    this.running = false
    window.clearTimeout(this.timer)
    this.buttonTarget.textContent = "继续演示"
    this.statusTarget.textContent = "演示已暂停，可以观察当前取出的两个字母。"
  }

  reset() {
    window.clearTimeout(this.timer)
    this.running = false
    this.started = false
    this.finished = false
    this.nextAction = null
    this.groupIndex = 0
    this.pairIndex = 0
    this.foundCount = 0
    this.completedSizes = []

    ;[this.firstSlotTarget, this.secondSlotTarget].forEach((slot) => {
      slot.getAnimations().forEach((animation) => animation.cancel())
      slot.style.opacity = ""
      slot.style.transform = ""
      slot.style.visibility = "visible"
    })

    this.pointTargets.forEach((point) => {
      point.classList.remove("border-amber-500", "bg-amber-400", "opacity-0", "opacity-20", "scale-50", "scale-75")
    })
    this.pairTargets.forEach((pair) => pair.classList.add("opacity-20"))
    this.groupTargets.forEach((group) => group.classList.remove("border-amber-300", "bg-amber-50"))
    this.setSlot(this.firstSlotTarget, "?")
    this.setSlot(this.secondSlotTarget, "?")
    this.currentPairTarget.textContent = "等待组合"
    this.countTarget.textContent = "0"
    this.sumTarget.textContent = "等待分组计数"
    this.statusTarget.textContent = "先从待选区拿出第一个字母 A。"
    this.buttonTarget.textContent = "播放演示"
    this.conclusionTarget.classList.add("opacity-40")
  }

  beginGroup() {
    if (!this.running) return
    if (this.groupIndex >= this.groups.length) {
      this.finish()
      return
    }

    const group = this.groups[this.groupIndex]
    this.pairIndex = 0
    this.firstSlotTarget.style.visibility = "visible"
    this.setSlot(this.firstSlotTarget, group.first)
    this.setSlot(this.secondSlotTarget, "?")
    this.currentPairTarget.textContent = `${group.first} 已经拿出`
    this.statusTarget.textContent = `拿出 ${group.first}，接下来让它依次和右边剩余的字母组合。`

    this.pointTargets.forEach((point, index) => {
      const completed = index < this.groupIndex
      const taken = index === this.groupIndex
      point.classList.toggle("opacity-20", completed)
      point.classList.toggle("scale-75", completed)
      point.classList.toggle("opacity-0", taken)
      point.classList.toggle("scale-50", taken)
    })
    this.groupTargets.forEach((row, index) => {
      const active = index === this.groupIndex
      row.classList.toggle("border-amber-300", active)
      row.classList.toggle("bg-amber-50", active)
    })
    this.animateIn(this.firstSlotTarget)
    this.schedule(() => this.showPair(), 700)
  }

  showPair() {
    if (!this.running) return
    const group = this.groups[this.groupIndex]
    const pair = group.pairs[this.pairIndex]
    const partner = pair[1]
    const pairName = pair.join("")

    this.setSlot(this.secondSlotTarget, partner)
    this.animateIn(this.secondSlotTarget)
    this.pointTargets.forEach((point, index) => {
      const isPartner = point.dataset.value === partner
      const isFirst = index === this.groupIndex
      point.classList.toggle("border-amber-500", isPartner || isFirst)
      point.classList.toggle("bg-amber-400", isPartner || isFirst)
    })
    this.pairTargets.find((item) => item.dataset.pair === pairName)?.classList.remove("opacity-20")

    this.foundCount += 1
    this.currentPairTarget.textContent = pairName
    this.countTarget.textContent = String(this.foundCount)
    this.statusTarget.textContent = `${group.first} 和 ${partner} 组成 ${pairName}，把这一组加入结果。`
    this.pairIndex += 1

    if (this.pairIndex < group.pairs.length) {
      this.schedule(() => this.clearPartner(), 720)
    } else {
      this.schedule(() => this.completeGroup(), 900)
    }
  }

  clearPartner() {
    if (!this.running) return
    const first = this.groups[this.groupIndex].first
    this.pointTargets.forEach((point) => {
      const isFirst = point.dataset.value === first
      point.classList.toggle("border-amber-500", isFirst)
      point.classList.toggle("bg-amber-400", isFirst)
    })
    this.setSlot(this.secondSlotTarget, "?")
    this.schedule(() => this.showPair(), 260)
  }

  completeGroup() {
    if (!this.running) return
    const group = this.groups[this.groupIndex]
    this.completedSizes.push(group.pairs.length)
    this.sumTarget.textContent = `${this.completedSizes.join(" + ")} = ${this.foundCount}`
    this.statusTarget.textContent = `${group.first} 的 ${group.pairs.length} 种组合已经找完。${group.first} 退出，下一轮不再出现。`

    this.pointTargets[this.groupIndex].classList.add("opacity-20", "scale-75")
    this.pointTargets[this.groupIndex].classList.remove("opacity-0", "scale-50", "border-amber-500", "bg-amber-400")
    this.animateOut(this.firstSlotTarget)
    this.setSlot(this.secondSlotTarget, "?")
    this.groupIndex += 1
    this.schedule(() => this.beginGroup(), 850)
  }

  finish() {
    this.running = false
    this.finished = true
    this.nextAction = null
    this.setSlot(this.firstSlotTarget, "?")
    this.setSlot(this.secondSlotTarget, "?")
    this.groupTargets.forEach((group) => group.classList.remove("border-amber-300", "bg-amber-50"))
    this.pointTargets.forEach((point) => point.classList.add("opacity-20", "scale-75"))
    this.currentPairTarget.textContent = "全部完成"
    this.sumTarget.textContent = "4 + 3 + 2 + 1 = 10"
    this.statusTarget.textContent = "最后只剩 E，无法再组成一对，所以演示结束。"
    this.buttonTarget.textContent = "重新播放"
    this.conclusionTarget.classList.remove("opacity-40")
  }

  schedule(action, delay) {
    this.nextAction = action
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => {
      this.nextAction = null
      action()
    }, this.reduceMotion ? 40 : delay)
  }

  setSlot(target, value) {
    target.textContent = value
    target.classList.toggle("text-slate-300", value === "?")
    target.classList.toggle("text-slate-950", value !== "?")
  }

  animateIn(target) {
    target.getAnimations().forEach((animation) => animation.cancel())
    target.style.visibility = "visible"
    target.style.opacity = "1"
    target.style.transform = ""
    if (this.reduceMotion) return
    target.animate(
      [{ opacity: 0, transform: "translateY(72px) scale(.65)" }, { opacity: 1, transform: "translateY(0) scale(1)" }],
      { duration: 520, easing: "cubic-bezier(.22, 1, .36, 1)" },
    )
  }

  animateOut(target) {
    target.getAnimations().forEach((animation) => animation.cancel())
    if (this.reduceMotion) {
      target.style.visibility = "hidden"
      return
    }

    const animation = target.animate(
      [{ opacity: 1, transform: "scale(1)" }, { opacity: 0, transform: "translateY(-18px) scale(.5)" }],
      { duration: 420, easing: "ease-in" },
    )
    animation.onfinish = () => { target.style.visibility = "hidden" }
  }
}
